import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

// Simple icon helpers
const EyeIcon = ({ show }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {show ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const CompleteSetupModal = () => {
  const { username: authUsername, role, mustChangePassword, updateSessionUser, logout } = useAuth();

  const [form, setForm] = useState({
    currentUsername: authUsername || "",
    newUsername: authUsername || "",
    newPassword: "",
    confirmPassword: "",
    name: "",
    email: "",
    mobileNumber: "",
    dateOfBirth: "",
    startDate: "",
    timezone: "Asia/Kolkata",
    workPermit: "Citizen",
    workPermitExpiry: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // { available: true/false, checking: bool }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Reset and fetch initial profile to prefill
  useEffect(() => {
    if (!authUsername || !mustChangePassword) return;
    setSuccess(false);
    setError("");
    setLoading(false);
    setShowPassword(false);
    setShowConfirm(false);
    setUsernameStatus(null);
    (async () => {
      try {
        const token = sessionStorage.getItem("jwtToken");
        const res = await fetch(`/timesheets/users/${authUsername}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const p = await res.json();
          setForm({
            currentUsername: authUsername,
            newUsername: authUsername,
            newPassword: "",
            confirmPassword: "",
            name: p.name || "",
            email: p.email || "",
            mobileNumber: p.mobileNumber || "",
            dateOfBirth: p.dateOfBirth || "",
            startDate: p.startDate || "",
            timezone: p.timezone || "Asia/Kolkata",
            workPermit: p.workPermit || "Citizen",
            workPermitExpiry: p.workPermitExpiry || "",
          });
        }
      } catch (e) {
        console.error("Error prefetching user profile for setup:", e);
      }
    })();
  }, [authUsername, mustChangePassword]);

  const isAmericaTz = (() => {
    const tz = (form.timezone || "America/New_York").toLowerCase();
    return tz.startsWith("america/") || tz.includes("york") || tz.includes("est") || tz.includes("edt") || tz.includes("cst") || tz.includes("pst") || tz.includes("us") || tz.includes("america");
  })();

  const showExpiryField = isAmericaTz && form.workPermit && form.workPermit.toLowerCase() !== "citizen";

  // Check username availability on change
  const handleUsernameBlur = async () => {
    const u = form.newUsername.trim();
    if (!u) {
      setUsernameStatus(null);
      return;
    }
    if (u.toLowerCase() === form.currentUsername.toLowerCase()) {
      setUsernameStatus({ available: true, message: "Current username" });
      return;
    }
    setUsernameStatus({ checking: true });
    try {
      const res = await fetch(`/timesheets/checkUsername?username=${encodeURIComponent(u)}`);
      if (res.ok) {
        const data = await res.json();
        setUsernameStatus({ available: data.available, message: data.available ? "Username is available!" : "Username is already taken" });
      }
    } catch {
      setUsernameStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.newPassword) {
      setError("Please set a new password.");
      return;
    }
    if (form.newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!form.name || !form.name.trim()) {
      setError("Please enter your full name.");
      return;
    }
    if (!form.email || !form.email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!form.mobileNumber || !form.mobileNumber.trim()) {
      setError("Please enter your mobile number.");
      return;
    }
    if (!form.dateOfBirth) {
      setError("Please select your date of birth.");
      return;
    }
    if (role !== "client_manager" && !form.startDate) {
      setError("Please select your start date.");
      return;
    }
    if (!form.timezone) {
      setError("Please select your timezone.");
      return;
    }
    if (isAmericaTz && role !== "client_manager") {
      if (!form.workPermit) {
        setError("Please select your work permit status.");
        return;
      }
      if (form.workPermit !== "Citizen" && !form.workPermitExpiry) {
        setError("Please enter your work permit expiry date.");
        return;
      }
    }
    if (usernameStatus && usernameStatus.available === false) {
      setError("Please choose an available username.");
      return;
    }

    setLoading(true);
    try {
      const token = sessionStorage.getItem("jwtToken");
      const res = await fetch("/timesheets/users/completeSetup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentUsername: form.currentUsername,
          newUsername: form.newUsername.trim(),
          newPassword: form.newPassword,
          name: form.name.trim(),
          email: form.email.trim(),
          mobileNumber: form.mobileNumber ? form.mobileNumber.trim() : "",
          dateOfBirth: form.dateOfBirth || null,
          startDate: form.startDate || null,
          timezone: form.timezone,
          workPermit: form.workPermit,
          workPermitExpiry: form.workPermit === "Citizen" ? null : form.workPermitExpiry,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        setTimeout(() => {
          updateSessionUser({
            token: data.token,
            username: data.username,
            name: data.name,
            role: data.role,
            mustChangePassword: false,
          });
        }, 1200);
      } else {
        setError(data.error || "Failed to complete setup. Please try again.");
      }
    } catch (err) {
      setError("A network error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!mustChangePassword) return null;

  return (
    <AnimatePresence>
      <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "rgba(15, 23, 42, 0.75)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          style={{
            background: "#FFFFFF",
            borderRadius: 20,
            maxWidth: 620,
            width: "100%",
            maxHeight: "90vh",
            overflowY: "auto",
            padding: "32px 36px",
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.35)",
            border: "1px solid #E2E8F0"
          }}
        >
          {success ? (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ textAlign: "center", padding: "40px 20px" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#DCFCE7", color: "#16A34A", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: "1.8rem" }}>
                ✓
              </div>
              <h3 style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0F172A", marginBottom: 8 }}>Account Setup Completed!</h3>
              <p style={{ color: "#64748B", fontSize: "0.9rem" }}>Redirecting you to your dashboard...</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} autoComplete="off">
              {/* Header */}
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
                <div>
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, color: "#2563EB", textTransform: "uppercase", letterSpacing: "0.08em" }}>First-Time Login Setup</span>
                  <h2 style={{ fontSize: "1.28rem", fontWeight: 800, color: "#0F172A", marginTop: 2 }}>Welcome to Skypa Systems! 👋</h2>
                  <p style={{ fontSize: "0.82rem", color: "#64748B", marginTop: 4 }}>
                    Please set your permanent password and complete your registration details to continue.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={logout}
                  title="Logout"
                  style={{ background: "#F1F5F9", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: "0.76rem", fontWeight: 600, color: "#64748B", cursor: "pointer" }}
                >
                  Logout
                </button>
              </div>

              {error && (
                <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626", fontSize: "0.82rem", fontWeight: 600, marginBottom: 18 }}>
                  {error}
                </div>
              )}

              {/* Section 1: Security & Credentials */}
              <div style={{ background: "#F8FBFF", border: "1px solid #DBEAFE", borderRadius: 14, padding: "18px 20px", marginBottom: 20 }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1E3A8A", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>🔒</span> Account Credentials
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
                  {/* Username (Editable) */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                        Username <span style={{ color: "#2563EB", fontWeight: 500 }}>(You can change this)</span>
                      </label>
                      {usernameStatus && (
                        <span style={{ fontSize: "0.72rem", fontWeight: 600, color: usernameStatus.available ? "#059669" : "#DC2626" }}>
                          {usernameStatus.checking ? "Checking..." : usernameStatus.message}
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      required
                      autoComplete="off"
                      value={form.newUsername}
                      onChange={e => {
                        setForm({ ...form, newUsername: e.target.value.replace(/\s+/g, '') });
                        setUsernameStatus(null);
                      }}
                      onBlur={handleUsernameBlur}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.88rem", fontWeight: 600, color: "#0F172A", background: "#FFFFFF", outline: "none" }}
                    />
                  </div>

                  {/* Password & Confirm Password */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                        New Permanent Password <span style={{ color: "#EF4444" }}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type={showPassword ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          placeholder="Min 8 characters"
                          value={form.newPassword}
                          onChange={e => setForm({ ...form, newPassword: e.target.value })}
                          style={{ width: "100%", padding: "9px 36px 9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.88rem", color: "#0F172A", outline: "none", boxSizing: "border-box" }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#64748B", display: "flex" }}
                        >
                          <EyeIcon show={showPassword} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                        Confirm Password <span style={{ color: "#EF4444" }}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          type={showConfirm ? "text" : "password"}
                          required
                          autoComplete="new-password"
                          placeholder="Re-enter password"
                          value={form.confirmPassword}
                          onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                          style={{ width: "100%", padding: "9px 36px 9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.88rem", color: "#0F172A", outline: "none", boxSizing: "border-box" }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#64748B", display: "flex" }}
                        >
                          <EyeIcon show={showConfirm} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Personal Profile Details */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 14, padding: "18px 20px", marginBottom: 24 }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#334155", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                  <span>📋</span> Profile Details
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {/* Full Name */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Full Name <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Enter full name"
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", background: "#FFFFFF", outline: "none" }}
                    />
                  </div>

                  {/* Email Address */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Email Address <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="Enter email address"
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value.trim() })}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", background: "#FFFFFF", outline: "none" }}
                    />
                  </div>

                  {/* Mobile Number */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Mobile Number <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Enter mobile number"
                      value={form.mobileNumber}
                      onChange={e => setForm({ ...form, mobileNumber: e.target.value })}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", background: "#FFFFFF", outline: "none" }}
                    />
                  </div>

                  {/* Date of Birth */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Date of Birth <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={form.dateOfBirth}
                      onChange={e => setForm({ ...form, dateOfBirth: e.target.value })}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", outline: "none" }}
                    />
                  </div>

                  {/* Start Date */}
                  {role !== "client_manager" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                        Start Date <span style={{ color: "#EF4444" }}>*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={form.startDate}
                        onChange={e => setForm({ ...form, startDate: e.target.value })}
                        style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", outline: "none" }}
                      />
                    </div>
                  )}

                  {/* Timezone */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                      Timezone <span style={{ color: "#EF4444" }}>*</span>
                    </label>
                    <select
                      required
                      value={form.timezone}
                      onChange={e => setForm({ ...form, timezone: e.target.value })}
                      style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", background: "#fff", outline: "none" }}
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata (India)</option>
                      <option value="America/New_York">America/New_York (US)</option>
                    </select>
                  </div>

                  {/* America Timezone Work Permit */}
                  {isAmericaTz && role !== "client_manager" && (
                    <>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                          Work Permit <span style={{ color: "#EF4444" }}>*</span>
                        </label>
                        <select
                          required
                          value={form.workPermit}
                          onChange={e => setForm({
                            ...form,
                            workPermit: e.target.value,
                            workPermitExpiry: e.target.value === "Citizen" ? "" : form.workPermitExpiry
                          })}
                          style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", background: "#fff", outline: "none" }}
                        >
                          <option value="Citizen">Citizen</option>
                          <option value="H1b">H1b</option>
                          <option value="GC">GC</option>
                          <option value="OPT">OPT</option>
                        </select>
                      </div>

                      {showExpiryField && (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <label style={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>
                            Work Permit Expiry Date <span style={{ color: "#EF4444" }}>*</span>
                          </label>
                          <input
                            type="date"
                            required
                            value={form.workPermitExpiry}
                            onChange={e => setForm({ ...form, workPermitExpiry: e.target.value })}
                            style={{ padding: "9px 12px", borderRadius: 8, border: "1.5px solid #CBD5E1", fontSize: "0.84rem", color: "#0F172A", outline: "none" }}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    padding: "11px 28px",
                    borderRadius: 10,
                    border: "none",
                    background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                    color: "#FFFFFF",
                    fontSize: "0.9rem",
                    fontWeight: 700,
                    cursor: loading ? "not-allowed" : "pointer",
                    boxShadow: "0 4px 14px rgba(37, 99, 235, 0.35)",
                    transition: "all 0.2s"
                  }}
                >
                  {loading ? "Saving & Finalizing..." : "Complete Setup & Enter Dashboard →"}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CompleteSetupModal;
